/**
 * Shared input + output primitives used by every field-takeoff calculator.
 *
 * Every numeric input goes through this module so all 8 calcs behave the
 * same way: empty placeholder when 0/undefined, click-to-select on focus,
 * no React "controlled vs uncontrolled" warnings, no literal "0" the user
 * has to delete before typing.
 */

import React from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../ui/Button';

/** Display helper — coerce undefined/NaN/0 to empty string for inputs. */
export const numInput = (n: number | null | undefined): number | '' => {
  if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return '';
  return n;
};

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (n: number) => void;
  unit?: string;
  step?: string | number;
  placeholder?: string;
  min?: number;
}

export const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange, unit, step = '0.01', placeholder = '0', min }) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1.5">
      <span className="label-ui">{label}</span>
      {unit && <span className="label-ui">{unit}</span>}
    </div>
    <input
      type="number"
      step={step}
      min={min}
      value={numInput(value)}
      placeholder={placeholder}
      onFocus={(e) => e.target.select()}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="w-full h-10 px-3 text-sm font-data tabular-nums text-navy-950 bg-white border border-acero-300 rounded-xl transition-[border-color,box-shadow] duration-150 placeholder:text-acero-400 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
    />
  </label>
);

interface SelectFieldProps<T extends string> {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}

export function SelectField<T extends string>({ label, value, onChange, options }: SelectFieldProps<T>) {
  return (
    <label className="block">
      <span className="label-ui block mb-1.5">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full h-10 px-3 text-sm text-navy-950 bg-white border border-acero-300 rounded-xl transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 cursor-pointer"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

interface OutputRowProps {
  label: string;
  value: string;
  emphasis?: 'primary' | 'normal' | 'muted';
  hint?: string;
}

export const OutputRow: React.FC<OutputRowProps> = ({ label, value, emphasis = 'normal', hint }) => {
  const valueClass =
    emphasis === 'primary' ? 'text-2xl font-data font-semibold text-navy-950 tabular-nums'
    : emphasis === 'muted' ? 'text-sm font-data text-acero-500 tabular-nums'
    : 'text-base font-data font-medium text-navy-950 tabular-nums';
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <div className="label-ui">{label}</div>
        {hint && <div className="text-[11px] text-acero-500 mt-0.5">{hint}</div>}
      </div>
      <div className={valueClass}>{value}</div>
    </div>
  );
};

interface OutputCardProps {
  title?: string;
  children: React.ReactNode;
}

export const OutputCard: React.FC<OutputCardProps> = ({ title, children }) => (
  <div className="bg-acero-50 border border-acero-200 rounded-2xl p-4">
    {title && <div className="label-ui mb-2">{title}</div>}
    <div className="divide-y divide-acero-200">{children}</div>
  </div>
);

interface AddToEstimateButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export const AddToEstimateButton: React.FC<AddToEstimateButtonProps> = ({ label, onClick, disabled }) => (
  <Button variant="secondary" className="w-full" onClick={onClick} disabled={disabled} leftIcon={<Plus size={14} />}>
    {label}
  </Button>
);

interface SectionHeaderProps {
  title: string;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
  <div className="label-ui -mb-2">{title}</div>
);
